import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import { on } from '@ember/modifier';
import { array } from '@ember/helper';
import { trackedFunction } from 'reactiveweb/function';
import { dropTask } from 'ember-concurrency';
import Button from 'tio-ui/components/button';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
import Select from 'tio-ui/components/select';
import Input from 'tio-ui/components/input';
import Flatpickr from 'tio-common/flatpickr';
import PreviewTable from '../report-builder/preview-table';
import SortPanel from '../report-builder/sort-panel';
import {
  fetchDistinctValues,
  shouldFetchDistinctValues,
} from '../../utils/distinct-values-resolver';
import type { ColumnAggregationMap, DateGroupingMap } from '../../utils/aggregation-types';
import type { ColumnFormatMap, AppContext } from '../../utils/column-format-options';
import { getReportRequest } from '../../requests/report';
import { SkipCache } from '@warp-drive/core-types/request';
import { query } from '@warp-drive/utilities/json-api';
import type Owner from '@ember/owner';
import {
  isDateType as _isDateType,
  isNumericType as _isNumericType,
  isBooleanType as _isBooleanType,
  hasEnumValues as _hasEnumValues,
  getEnumFilterItems as _getEnumFilterItems,
  hasDistinctValues as _hasDistinctValues,
  getDistinctItems as _getDistinctItems,
  isTextType as _isTextType,
  getFilterValue as _getFilterValue,
  getFilterRangeStart as _getFilterRangeStart,
  getFilterRangeEnd as _getFilterRangeEnd,
} from '../../utils/filter-type-helpers';
import {
  getColumnDivisor,
  rawToDisplayNumber,
  displayToRawNumber,
} from '../../utils/filter-number-format';
import {
  buildDateWindowForField,
  dateRangeForField,
  dateWindowWarningMessage,
  laterIsoDate,
  type DateRange,
} from '../../utils/date-filter-window';
import {
  resolveFieldsFromEnhancedEndpoint,
  type DatasetConfig,
  type EnhancedFieldsResponse,
} from '../../utils/field-filter-pipeline';
import { exportReport as exportReportRequest } from 'tio-data/requests/search';
import { sqlExportReport } from 'tio-data/requests/sql-dataset';
import { buildReportSearchBody } from '../../utils/report-search-body';
import { fieldsRequestFor } from '../../utils/search-requests';
import type FeaturesService from 'tio-common/services/features';
import {
  PhFileText,
  PhDownloadSimple,
  PhArrowLeft,
  PhFunnel,
  PhX,
  PhSlidersHorizontal,
  PhCaretLeft,
  PhArrowCounterClockwise,
} from 'ember-phosphor-icons';
import type { Store as StoreService } from '@warp-drive/core';
import type { Company as CompanyModel } from 'tio-data/data/resources/company.type';
import type ReportTrackingService from '../../services/report-tracking';
import type { ReportTrackingSession } from '../../services/report-tracking';
import type { FieldOption } from '../../utils/opensearch-field-mapper';
import {
  getDatasetFromModelType,
  getCompanyFilterField,
  getSearchRequestModelType,
  isCrossCompanyCapable,
  isSqlDataset,
} from '../../utils/dataset-utils';
import {
  type SortEntry,
  addSort,
  removeSort,
  moveSort,
  toggleSortDirection,
  setSortDirection,
  migrateLegacySortByDirection,
} from '../../utils/sort-helpers';
import {
  getOperatorInfo,
  getOperatorsForCategory,
  type FilterOperator,
  type FilterLayout,
} from '../../utils/filter-operators';
import { getRelativeDateItems, getRelativeDateLabel } from '../../utils/relative-date-resolver';

// Controller interface — reportId and optional companyId travel in the URL
interface ReportsViewerController {
  reportId?: string;
  companyId?: string;
}

interface ReportAttributes {
  name?: string;
  'report-type'?: string;
  report_type?: string;
  reportType?: string;
  metadata?: Record<string, unknown>;
  'search-config'?: Record<string, unknown>;
  search_config?: Record<string, unknown>;
  searchConfig?: Record<string, unknown>;
  'updated-at'?: string;
  updated_at?: string;
  updatedAt?: string;
}

type CompanyFilterOperator = 'in' | 'not_in';

export interface ReportViewPageSignature {
  Args: {
    controller: ReportsViewerController;
    viewedFrom?: 'admin' | 'partner';
    /**
     * Report types (lowercase) this viewer may open. Guards direct-URL access:
     * partner admin passes ["tas"] so published reports of other products stay
     * inaccessible there even with a known reportId. Omit for no restriction.
     */
    allowedReportTypes?: string[];
    onBack?: () => void;
  };
}

/**
 * Key-wise comparison of two filter-state maps. Multi-select emits set-equal
 * arrays in varying order (deselect + reselect re-appends), so arrays compare
 * order-insensitively.
 */
function filterStateDiffers(a: Record<string, unknown>, b: Record<string, unknown>) {
  const norm = (v: unknown) =>
    Array.isArray(v) ? JSON.stringify([...v].sort()) : JSON.stringify(v ?? null);
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if (norm(a[k]) !== norm(b[k])) return true;
  }
  return false;
}

class ReportViewPage extends Component<ReportViewPageSignature> {
  @service declare store: StoreService;
  @service declare reportTracking: ReportTrackingService;
  // WarpDrive wave 1.5: features.ENV.apiKey feeds the raw-fetch report-export
  // helper (tio-data is owner-free, so the per-app api key is passed in).
  @service declare features: FeaturesService;

  private _lastLoadedReportId: string | undefined;
  private reportTrackingSession: ReportTrackingSession | undefined;

  /** localStorage key for persisting user view state per report */
  private _viewStateKey = (reportId: string) => `tio-report-view:${reportId}`;

  get appContext(): AppContext {
    return this.args.viewedFrom === 'partner' ? 'employee' : 'admin';
  }

  /** Save current filter/sort state to localStorage */
  private _saveViewState = () => {
    if (!this.reportId) return;
    try {
      const state: Record<string, unknown> = {};
      if (Object.keys(this.userFilterValues).length > 0) {
        state['filterValues'] = this.userFilterValues;
      }
      if (Object.keys(this.userFilterOperators).length > 0) {
        state['filterOperators'] = this.userFilterOperators;
      }
      if (this.sorts.length > 0) {
        state['sorts'] = this.sorts;
      }
      if (Object.keys(state).length > 0) {
        localStorage.setItem(this._viewStateKey(this.reportId), JSON.stringify(state));
      } else {
        localStorage.removeItem(this._viewStateKey(this.reportId));
      }
    } catch {
      // localStorage unavailable or quota exceeded — silently ignore
    }
  };

  /** Restore filter/sort state from localStorage, overlaying config defaults */
  private _restoreViewState = () => {
    if (!this.reportId) return;
    try {
      const raw = localStorage.getItem(this._viewStateKey(this.reportId));
      if (!raw) return;
      const state = JSON.parse(raw) as Record<string, unknown>;

      const savedFilters = state['filterValues'] as Record<string, unknown> | undefined;
      if (savedFilters && Object.keys(savedFilters).length > 0) {
        this.userFilterValues = savedFilters;
      }

      const savedOperators = state['filterOperators'] as Record<string, FilterOperator> | undefined;
      if (savedOperators && Object.keys(savedOperators).length > 0) {
        this.userFilterOperators = savedOperators;
      }

      const savedSorts = state['sorts'] as SortEntry[] | undefined;
      if (savedSorts && Array.isArray(savedSorts) && savedSorts.length > 0) {
        this.sorts = savedSorts;
      } else {
        const savedSort = state['sortField'] as string | undefined;
        if (savedSort) {
          const field = savedSort.replace(/:(?:asc|desc)$/, '');
          const dir: 'asc' | 'desc' = savedSort.endsWith(':desc') ? 'desc' : 'asc';
          this.sorts = [{ key: field, direction: dir }];
        }
      }
    } catch {
      // Corrupted or unavailable — silently ignore
    }
  };

  constructor(owner: Owner, args: ReportViewPageSignature['Args']) {
    super(owner, args);
    registerDestructor(this, this.endReportTrackingSession);
    const id = args.controller.reportId;
    if (id) {
      this._lastLoadedReportId = id;
      this.reportId = id;
      this.loadReport(id);
    }
  }

  // React to reportId query param changes (back/forward nav, in-route transitions)
  _reportIdWatcher = trackedFunction(this, async () => {
    const id = this.args.controller.reportId;
    if (id && id !== this._lastLoadedReportId) {
      this._lastLoadedReportId = id;
      this.reportId = id;
      await this.loadReport(id);
    }
  });

  @tracked reportName = '';
  @tracked updatedAt = '';
  @tracked reportId = '';
  @tracked companyId?: string;
  @tracked companyIds: string[] = [];
  @tracked companyFilterOperator: CompanyFilterOperator = 'in';
  @tracked modelType = 'TasProgramInstance';
  @tracked showIndividualApplicationsOnly = true;
  @tracked datasetConfig: DatasetConfig | undefined;
  @tracked isGlobalReport = false;
  @tracked isLoading = true;
  @tracked isLoadingFields = false;
  @tracked loadError = '';
  @tracked sorts: SortEntry[] = [];
  private savedSorts: SortEntry[] = [];
  // Saved-report baseline for filters (config defaults), used to detect local
  // overrides and offer a "reset to saved" action.
  private savedUserFilterValues: Record<string, unknown> = {};
  private savedUserFilterOperators: Record<string, FilterOperator> = {};
  @tracked page = 1;
  @tracked limit = 25;

  // Field metadata for columns and filters
  @tracked allFields: FieldOption[] = [];
  @tracked distinctValuesMap: Record<string, string[]> = {};

  // Parsed from search_config
  @tracked columnKeys: string[] = [];
  @tracked userFilterKeys: string[] = [];
  @tracked buildFilterValues: Record<string, unknown> = {};
  @tracked buildFilterLayout: FilterLayout = [];
  @tracked groupByFieldKeys: string[] = [];
  @tracked columnAggregationMap: ColumnAggregationMap = {};
  @tracked dateGroupingIntervals: DateGroupingMap = {};
  @tracked columnLabelMap: Record<string, string> = {};
  @tracked columnAltTextMap: Record<string, string> = {};
  @tracked columnFormatMap: ColumnFormatMap = {};
  @tracked userFilterValues: Record<string, unknown> = {};
  @tracked userFilterOperators: Record<string, FilterOperator> = {};
  @tracked isFilterSidebarOpen = true;

  // Companies for global report selector
  companiesData = trackedFunction(this, async () => {
    const { content } = (await this.store.request(
      query<CompanyModel>('company', {
        'filter[is-active]': 1,
        'page[limit]': 400,
      })
    )) as { content: { data: CompanyModel[] } };
    return content.data.map((c) => ({
      key: c.id as string,
      label: c.name || `Company ${c.id}`,
    }));
  });

  get companies() {
    return this.companiesData.value || [];
  }

  get selectedCompanyIds() {
    return this.companyIds;
  }

  get activeCompanyIds() {
    return this.isGlobalReport ? this.companyIds : [];
  }

  get companyFilterOperatorOptions() {
    return [
      { key: 'in', label: 'one of' },
      { key: 'not_in', label: 'not one of' },
    ];
  }

  /** Whether the report was opened from a company-scoped context (e.g. company page) */
  get contextCompanyId() {
    return this.args.controller.companyId;
  }

  get needsCompanySelector() {
    // Hide company dropdown when viewing from a company-scoped context
    if (this.contextCompanyId) return false;
    return this.isGlobalReport && !this.isLoading;
  }

  get searchRequestModelType() {
    return getSearchRequestModelType(this.modelType, this.showIndividualApplicationsOnly);
  }

  /** Reports need field metadata before we can query */
  get canShowData() {
    if (this.isLoadingFields) return false;
    if (this.loadError) return false;

    return this.allFields.length > 0 && this.selectedColumns.length > 0;
  }

  get controller() {
    return this.args.controller;
  }

  // --- Derived state ---

  get selectedColumns(): FieldOption[] {
    return this.columnKeys
      .map((key) => this.allFields.find((f) => f.key === key))
      .filter(Boolean) as FieldOption[];
  }

  /** Derive the company filter field from modelType via dataset reverse-mapping */
  get companyFilterField(): string | undefined {
    const dataset = getDatasetFromModelType(this.modelType);
    return (
      this.datasetConfig?.companyFilterField ||
      (dataset ? getCompanyFilterField(dataset) : undefined)
    );
  }

  get isAggregated() {
    return this.groupByFieldKeys.length > 0;
  }

  get userFilters(): FieldOption[] {
    return this.userFilterKeys
      .map((key) => this.allFields.find((f) => f.key === key))
      .filter(Boolean) as FieldOption[];
  }

  get hasUserFilters() {
    return this.userFilters.length > 0;
  }

  get hasMultipleSorts() {
    return this.sorts.length > 1;
  }

  get isSortDirty() {
    const saved = this.savedSorts;
    const current = this.sorts;
    if (saved.length !== current.length) return true;
    return saved.some((s, i) => s.key !== current[i]?.key || s.direction !== current[i]?.direction);
  }

  /** True when the current user-filter values/operators differ from the saved report config. */
  get isFilterDirty() {
    return (
      filterStateDiffers(this.userFilterValues, this.savedUserFilterValues) ||
      filterStateDiffers(this.userFilterOperators, this.savedUserFilterOperators)
    );
  }

  /**
   * Export-confirm gate: only VALUE divergence changes which rows a query
   * matches. An operator switched with no value entered (isFilterDirty via
   * the operators map) compiles to no clause, so it must not trigger the
   * "your export differs" dialog.
   */
  get exportFilterValuesDiverged() {
    return filterStateDiffers(this.userFilterValues, this.savedUserFilterValues);
  }

  /**
   * A saved default filter value whose field currently carries no value at
   * all — the export returns rows the saved report would filter out. Changed
   * (still-active) values don't widen the result set; they show as chips.
   * Detected independently of hasActiveChips so clearing one saved default
   * while other filters remain active still surfaces the widening warning.
   */
  get hasClearedSavedFilterValues() {
    const meaningful = (v: unknown) =>
      v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);
    const baseOf = (key: string) => key.split('__')[0] as string;
    const activeBases = new Set(
      Object.entries(this.userFilterValues)
        .filter(([, v]) => meaningful(v))
        .map(([key]) => baseOf(key))
    );
    return Object.entries(this.savedUserFilterValues).some(
      ([key, v]) => meaningful(v) && !activeBases.has(baseOf(key))
    );
  }

  get formattedDate() {
    if (!this.updatedAt) return '';
    return new Date(this.updatedAt).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  /** Active filter chips for display below the filter bar */
  get activeFilterChips(): Array<{ key: string; label: string; value: string }> {
    const chips: Array<{ key: string; label: string; value: string }> = [];

    for (const field of this.userFilters) {
      const op = this.detectOperator(field);
      const opLabel = getOperatorInfo(op).label;
      const displayLabel = this.getFieldDisplayLabel(field);

      if (op === 'is_empty' || op === 'is_not_empty') {
        chips.push({ key: field.key, label: displayLabel, value: opLabel });
      } else if (op === 'relative_date') {
        const preset = this.userFilterValues[`${field.key}__relative_date`];
        if (preset) {
          chips.push({
            key: field.key,
            label: displayLabel,
            value: getRelativeDateLabel(String(preset)),
          });
        }
      } else if (op === 'between') {
        // Explicit presence checks — a numeric bound of 0 is valid but falsy,
        // and a missing chip here would let the export confirmation omit or
        // misstate an active range filter.
        const hasBound = (v: unknown) => v !== undefined && v !== null && v !== '';
        const gteVal = this.userFilterValues[`${field.key}__gte`];
        const lteVal = this.userFilterValues[`${field.key}__lte`];
        if (hasBound(gteVal) || hasBound(lteVal)) {
          // displayNumber re-applies the cents divisor so a chip quotes what
          // the user typed (250), not the stored raw value (25000); it passes
          // non-numeric values through unchanged.
          const parts = [
            hasBound(gteVal) ? this.displayNumber(field.key, gteVal) : '…',
            hasBound(lteVal) ? this.displayNumber(field.key, lteVal) : '…',
          ];
          chips.push({ key: field.key, label: displayLabel, value: `${parts[0]} – ${parts[1]}` });
        }
      } else if (op === 'in' || op === 'not_in') {
        const vals = this.getMultiSelectValues(field);
        if (vals.length > 0) {
          chips.push({
            key: field.key,
            label: `${displayLabel} (${opLabel})`,
            value: vals.join(', '),
          });
        }
      } else {
        const val =
          op === 'eq'
            ? this.userFilterValues[field.key]
            : this.userFilterValues[`${field.key}__${op}`];
        if (val !== undefined && val !== null && val !== '') {
          const prefix = op === 'eq' ? '' : `${opLabel} `;
          chips.push({
            key: field.key,
            label: displayLabel,
            value: `${prefix}${this.displayNumber(field.key, val)}`,
          });
        }
      }
    }
    return chips;
  }

  get hasActiveChips() {
    return this.activeFilterChips.length > 0;
  }

  /** Resolve a field's display label, preferring custom columnLabelMap overrides */
  getFieldDisplayLabel = (field: FieldOption) => {
    return this.columnLabelMap[field.key] || field.label;
  };

  // --- Filter control helpers (delegating to shared utilities) ---

  isDateType = _isDateType;
  isNumericType = _isNumericType;
  isBooleanType = _isBooleanType;
  hasEnumValues = _hasEnumValues;
  isTextType = _isTextType;

  hasDistinctValues = (field: FieldOption) => _hasDistinctValues(field, this.distinctValuesMap);

  getDistinctItems = (field: FieldOption) => _getDistinctItems(field, this.distinctValuesMap);

  /** Enum dropdown items — live distinct set when fetched, else the full enum. */
  getEnumSelectItems = (field: FieldOption) => _getEnumFilterItems(field, this.distinctValuesMap);

  // --- Operator detection helpers ---

  /** Detect the current operator for a field.\n   * Uses explicitly stored operator if available, otherwise infers from filter value keys. */
  detectOperator = (field: FieldOption): FilterOperator => {
    // Prefer explicitly stored operator
    const stored = this.userFilterOperators[field.key];
    if (stored) return stored;

    // Fallback: infer from filter value keys
    const filters = this.userFilterValues;
    const hasGte =
      filters[`${field.key}__gte`] !== undefined && filters[`${field.key}__gte`] !== null;
    const hasLte =
      filters[`${field.key}__lte`] !== undefined && filters[`${field.key}__lte`] !== null;
    if (hasGte && hasLte) return 'between';

    const suffixes: FilterOperator[] = [
      'relative_date',
      'is_not_empty',
      'is_empty',
      'starts_with',
      'contains',
      'not_in',
      'in',
      'gte',
      'lte',
      'gt',
      'lt',
      'ne',
    ];
    for (const suffix of suffixes) {
      const val = filters[`${field.key}__${suffix}`];
      if (val !== undefined && val !== null) return suffix;
    }
    if (filters[field.key] !== undefined && filters[field.key] !== null) return 'eq';
    return 'eq';
  };

  /** Get display label for the detected operator */
  operatorLabel = (field: FieldOption) => {
    const op = this.detectOperator(field);
    return getOperatorInfo(op).label;
  };

  toggleFilterSidebar = () => {
    this.isFilterSidebarOpen = !this.isFilterSidebarOpen;
  };

  isNoValueOperator = (op: FilterOperator) => op === 'is_empty' || op === 'is_not_empty';
  isBetweenOp = (op: FilterOperator) => op === 'between';
  isMultiSelectOp = (op: FilterOperator) => op === 'in' || op === 'not_in';
  isRelativeDateOp = (op: FilterOperator) => op === 'relative_date';

  /** Get the field category for operator dropdown options */
  getCategory = (field: FieldOption) => {
    if (_isDateType(field)) return 'date' as const;
    if (_isNumericType(field)) return 'number' as const;
    if (_isBooleanType(field)) return 'boolean' as const;
    if (_hasEnumValues(field)) return 'select' as const;
    return 'text' as const;
  };

  /** Get operator select items for a field */
  getOperatorItems = (field: FieldOption) => {
    const category = this.getCategory(field);
    return getOperatorsForCategory(category).map((op) => ({
      key: op.key,
      label: op.label,
    }));
  };

  /** Handle operator change in view mode — clear old values, set new operator */
  handleOperatorChange = (fieldKey: string, newOp: string) => {
    if (!newOp) return;
    const op = newOp as FilterOperator;

    // Clear all existing filter entries for this field
    const vals = { ...this.userFilterValues };
    for (const key of Object.keys(vals)) {
      if (key === fieldKey || key.startsWith(`${fieldKey}__`)) {
        delete vals[key];
      }
    }

    // Set placeholder for new operator
    const info = getOperatorInfo(op);
    if (!info.requiresValue && !info.requiresRange) {
      if (op === 'is_empty') vals[`${fieldKey}__is_empty`] = true;
      else if (op === 'is_not_empty') vals[`${fieldKey}__is_not_empty`] = true;
    }

    this.userFilterValues = vals;

    // Store the explicit operator choice
    this.userFilterOperators = { ...this.userFilterOperators, [fieldKey]: op };

    this.page = 1;
    this._saveViewState();
  };

  /** Relative date preset items for the dropdown */
  relativeDateItems = getRelativeDateItems();

  /** Handle relative date preset selection */
  handleRelativeDateChange = (fieldKey: string, value: string) => {
    this.updateFilterValue(`${fieldKey}__relative_date`, value || undefined);
  };

  /** Get the value for a field using its detected operator */
  /** Divisor for a field's number-formatted column, or undefined. */
  private divisorFor(fieldKey: string): number | undefined {
    return getColumnDivisor(this.columnFormatMap, fieldKey);
  }

  /** Convert a stored raw number to its display string (applies divisor). */
  private displayNumber(fieldKey: string, raw: unknown): string {
    if (raw === undefined || raw === null || raw === '') return '';
    const num = Number(raw);
    if (isNaN(num)) return String(raw);
    return String(rawToDisplayNumber(num, this.divisorFor(fieldKey)));
  }

  /** Convert a user-entered display string to a stored raw number. */
  private storeNumber(fieldKey: string, value: string): number | undefined {
    if (!value) return undefined;
    const num = Number(value);
    if (isNaN(num)) return undefined;
    return displayToRawNumber(num, this.divisorFor(fieldKey));
  }

  getValueForOperator = (field: FieldOption) => {
    const op = this.detectOperator(field);
    const raw =
      op === 'eq' ? this.userFilterValues[field.key] : this.userFilterValues[`${field.key}__${op}`];
    if (Array.isArray(raw)) return raw.join(', ');
    if (this.divisorFor(field.key) && raw !== undefined && raw !== null && raw !== '') {
      return this.displayNumber(field.key, raw);
    }
    // Use a null check (not falsy) so a stored 0 renders as "0", not ""
    return raw != null ? String(raw) : '';
  };

  /** Get multi-select values for in/not_in operators */
  getMultiSelectValues = (field: FieldOption) => {
    const op = this.detectOperator(field);
    const key = op === 'eq' ? field.key : `${field.key}__${op}`;
    const raw = this.userFilterValues[key];
    if (Array.isArray(raw)) return raw as string[];
    if (typeof raw === 'string' && raw) return raw.split(',');
    return [];
  };

  /** Handle value change for operator-aware filters */
  handleValueForOperator = (fieldKey: string, operator: FilterOperator, value: string) => {
    if (operator === 'eq') {
      this.updateFilterValue(fieldKey, value || undefined);
    } else {
      this.updateFilterValue(`${fieldKey}__${operator}`, value || undefined);
    }
  };

  /** Handle multi-select change for in/not_in operators */
  handleMultiSelectChange = (fieldKey: string, operator: FilterOperator, keys: string[]) => {
    const filterKey = operator === 'eq' ? fieldKey : `${fieldKey}__${operator}`;
    this.updateFilterValue(filterKey, keys.length > 0 ? keys : undefined);
  };

  handleDateValueForOperator = (
    fieldKey: string,
    operator: FilterOperator,
    _dates: Date[],
    dateStr: string
  ) => {
    this.handleValueForOperator(fieldKey, operator, dateStr);
  };

  handleNumberForOperator = (fieldKey: string, operator: FilterOperator, value: string) => {
    const numValue = this.storeNumber(fieldKey, value);
    if (operator === 'eq') {
      this.updateFilterValue(fieldKey, numValue);
    } else {
      this.updateFilterValue(`${fieldKey}__${operator}`, numValue);
    }
  };

  filterWidthStyle = (field: FieldOption) => {
    let maxLen = 0;

    if (_isBooleanType(field)) {
      maxLen = 3; // "Yes" / "No" / "Any"
    } else if (_hasEnumValues(field)) {
      maxLen = Math.max(...(field.enumValues || []).map((v) => v.length), 0);
    } else if (_hasDistinctValues(field, this.distinctValuesMap)) {
      const vals = this.distinctValuesMap[field.key] || [];
      maxLen = Math.max(...vals.map((v) => v.length), 0);
    }

    // Pad for chevron + clear button + internal spacing; floor at 10ch
    const width = Math.max(maxLen + 6, 10);
    return htmlSafe(`min-width: ${width}ch`);
  };

  getFilterValue = (key: string) => _getFilterValue(key, this.userFilterValues);

  getFilterRangeStart = (key: string) => {
    const raw = _getFilterRangeStart(key, this.userFilterValues);
    return this.divisorFor(key) ? this.displayNumber(key, raw) : raw;
  };

  getFilterRangeEnd = (key: string) => {
    const raw = _getFilterRangeEnd(key, this.userFilterValues);
    return this.divisorFor(key) ? this.displayNumber(key, raw) : raw;
  };

  handleSelectChange = (fieldKey: string, value: string) => {
    this.updateFilterValue(fieldKey, value);
  };

  // ── Build-filter date window (clamp viewer pickers + warn on non-overlap) ──

  /** The build filter's hard date window for a field, or null if unbounded. */
  dateBuildWindow = (field: FieldOption): DateRange | null =>
    buildDateWindowForField(this.buildFilterValues, this.buildFilterLayout, field.key);

  /** Earliest date the viewer may pick — the build window's lower bound. */
  dateWindowMin = (field: FieldOption) => this.dateBuildWindow(field)?.gte ?? '';

  /** Latest date the viewer may pick — the build window's upper bound. */
  dateWindowMax = (field: FieldOption) => this.dateBuildWindow(field)?.lte ?? '';

  /**
   * `between` end-picker floor: the LATER of the chosen start and the window
   * minimum. A saved start that predates the window must not let the end picker
   * dip below the hard window bound (`start || windowMin` would).
   */
  dateRangeEndMin = (field: FieldOption) =>
    laterIsoDate(_getFilterRangeStart(field.key, this.userFilterValues), this.dateWindowMin(field));

  /**
   * Warning shown when the viewer's date selection reaches outside the build
   * window: either it doesn't overlap at all (empty result) or it extends
   * beyond the window (silently capped — e.g. picking "Past 90 Days" against a
   * report hardwired to the last 30). Empty when the selection sits fully
   * inside the window or the field has no hard window. Absolute pickers are
   * clamped, so in practice this fires for relative presets wider than it.
   */
  dateWindowWarning = (field: FieldOption): string =>
    dateWindowWarningMessage(
      dateRangeForField(this.userFilterValues, field.key),
      this.dateBuildWindow(field)
    );

  /**
   * The from/to range inputs only render for `between`, so any edit implies
   * between. Commit it explicitly — otherwise, with one bound filled,
   * detectOperator's fallback infers gte/lte and the operator visibly flips.
   */
  private commitBetween(fieldKey: string) {
    if (this.userFilterOperators[fieldKey] !== 'between') {
      this.userFilterOperators = { ...this.userFilterOperators, [fieldKey]: 'between' };
    }
  }

  handleDateStartChange = (fieldKey: string, _dates: Date[], dateStr: string) => {
    this.commitBetween(fieldKey);
    this.updateFilterValue(`${fieldKey}__gte`, dateStr || undefined);
  };

  handleDateEndChange = (fieldKey: string, _dates: Date[], dateStr: string) => {
    this.commitBetween(fieldKey);
    this.updateFilterValue(`${fieldKey}__lte`, dateStr || undefined);
  };

  handleNumberMinChange = (fieldKey: string, value: string) => {
    this.commitBetween(fieldKey);
    this.updateFilterValue(`${fieldKey}__gte`, this.storeNumber(fieldKey, value));
  };

  handleNumberMaxChange = (fieldKey: string, value: string) => {
    this.commitBetween(fieldKey);
    this.updateFilterValue(`${fieldKey}__lte`, this.storeNumber(fieldKey, value));
  };

  handleBooleanChange = (fieldKey: string, value: string) => {
    if (value === 'true') {
      this.updateFilterValue(fieldKey, true);
    } else if (value === 'false') {
      this.updateFilterValue(fieldKey, false);
    } else {
      this.updateFilterValue(fieldKey, '');
    }
  };

  /** Clear all values for a filter (used by chip dismiss) */
  dismissChip = (chipKey: string) => {
    const vals = { ...this.userFilterValues };
    // Remove all operator-suffixed keys for this field
    for (const key of Object.keys(vals)) {
      if (key === chipKey || key.startsWith(`${chipKey}__`)) {
        delete vals[key];
      }
    }
    this.userFilterValues = vals;
    this.page = 1;
    this._saveViewState();

    // Refresh cascading distinct values
    this.refreshDistinctValues();
  };

  // --- Actions ---

  loadReport = async (reportId: string) => {
    this.isLoading = true;
    this.loadError = '';
    this.reportName = '';
    this.updatedAt = '';
    this.companyId = undefined;
    this.companyIds = [];
    this.companyFilterOperator = 'in';
    this.modelType = 'TasProgramInstance';
    this.showIndividualApplicationsOnly = true;
    this.datasetConfig = undefined;
    this.isGlobalReport = false;
    this.isLoadingFields = false;
    this.columnKeys = [];
    this.userFilterKeys = [];
    this.buildFilterValues = {};
    this.groupByFieldKeys = [];
    this.columnAggregationMap = {};
    this.dateGroupingIntervals = {};
    this.columnLabelMap = {};
    this.columnAltTextMap = {};
    this.columnFormatMap = {};
    this.allFields = [];
    this.distinctValuesMap = {};
    // Invalidate any in-flight facet fetch from a previously-loaded report:
    // this component is reused across in-route report-ID changes, and a slow
    // response from the old report must not repopulate the cleared map.
    this.#distinctFetchEpoch++;
    this.sorts = [];
    this.userFilterValues = {};
    this.userFilterOperators = {};
    this.page = 1;
    this.limit = 25;
    // Export UI state is report-scoped too — this component is reused across
    // in-route report-ID changes, and report A's error or open confirmation
    // must not carry into report B.
    this.exportError = '';
    this.showExportFilterConfirm = false;

    try {
      const request = getReportRequest(reportId);
      request.cacheOptions = { [SkipCache]: true };
      const response = await this.store.request(request);
      const responseData = response.content as {
        data: { id: string; attributes: ReportAttributes };
      };
      const attrs = responseData?.data?.attributes;
      if (!attrs) return;

      // Product-scope guard (e.g. partner admin is TAS-only for now): block
      // before any state hydration, field fetches, or tracking-session start.
      // FAIL CLOSED: any PROVIDED allow-list is enforced — an empty list
      // allows nothing; only a truly omitted arg means unrestricted.
      const allowedTypes = this.args.allowedReportTypes?.map((type) => type.toLowerCase());
      if (allowedTypes) {
        const reportType = (
          (attrs['report-type'] ?? attrs.report_type ?? attrs.reportType) as string | undefined
        )?.toLowerCase();
        if (!reportType || !allowedTypes.includes(reportType)) {
          this.loadError = 'This report is not available.';
          return;
        }
      }

      this.reportName = (attrs['name'] as string) || 'Untitled Report';
      this.updatedAt = (attrs['updated-at'] ||
        attrs['updated_at'] ||
        attrs['updatedAt'] ||
        '') as string;

      // Try all possible key formats for the search config
      let rawConfig =
        attrs['search-config'] ?? attrs['search_config'] ?? attrs['searchConfig'] ?? {};
      // If the API returned the config as a JSON string, parse it
      if (typeof rawConfig === 'string') {
        try {
          rawConfig = JSON.parse(rawConfig);
        } catch {
          rawConfig = {};
        }
      }
      const config = rawConfig as Record<string, unknown>;

      this.companyId = (config['companyId'] ?? config['company_id']) as string | undefined;

      // When opened from a company-scoped context, lock to that company
      if (this.contextCompanyId) {
        this.companyId = this.contextCompanyId;
      }

      this.modelType = (config['modelType'] ??
        config['model_type'] ??
        'TasProgramInstance') as string;
      const savedIndividualApplicationsOnly =
        config['showIndividualApplicationsOnly'] ?? config['show_individual_applications_only'];
      this.showIndividualApplicationsOnly =
        typeof savedIndividualApplicationsOnly === 'boolean'
          ? savedIndividualApplicationsOnly
          : this.modelType === 'TasProgramInstance';
      this.isGlobalReport = !this.companyId;

      // Parse table columns (handle camelCase and snake_case keys)
      const tableSettings = (config['tableSettings'] || config['table_settings']) as
        Record<string, unknown> | undefined;
      if (tableSettings) {
        const cols = (tableSettings['selectedColumns'] || tableSettings['selected_columns']) as
          string[] | undefined;
        if (cols?.length) this.columnKeys = cols;

        const configSorts = tableSettings['sorts'] as SortEntry[] | undefined;
        if (configSorts && Array.isArray(configSorts) && configSorts.length > 0) {
          this.sorts = configSorts;
        } else {
          const sortBy = (tableSettings['sortBy'] || tableSettings['sort_by']) as
            string | undefined;
          const sortDir =
            ((tableSettings['sortDirection'] || tableSettings['sort_direction']) as string) ||
            'asc';
          this.sorts = migrateLegacySortByDirection(sortBy, sortDir);
        }
        this.savedSorts = [...this.sorts];

        const savedLabels = (tableSettings['columnLabels'] || tableSettings['column_labels']) as
          Record<string, string> | undefined;
        if (savedLabels && Object.keys(savedLabels).length > 0) {
          this.columnLabelMap = savedLabels;
        }

        const savedAltTexts = (tableSettings['columnAltTexts'] ||
          tableSettings['column_alt_texts']) as Record<string, string> | undefined;
        if (savedAltTexts && Object.keys(savedAltTexts).length > 0) {
          this.columnAltTextMap = savedAltTexts;
        }

        const savedFormats = (tableSettings['columnFormats'] || tableSettings['column_formats']) as
          ColumnFormatMap | undefined;
        if (savedFormats && Object.keys(savedFormats).length > 0) {
          this.columnFormatMap = savedFormats;
        }
      }

      // Parse build filters (applied silently)
      const buildFilters = (config['buildFilters'] || config['build_filters']) as
        Record<string, unknown> | undefined;
      if (buildFilters) {
        const bfv = (buildFilters['filterValues'] || buildFilters['filter_values']) as
          Record<string, unknown> | undefined;
        if (bfv && Object.keys(bfv).length > 0) {
          this.buildFilterValues = bfv;
        }
        const bfl = (buildFilters['filterLayout'] || buildFilters['filter_layout']) as
          FilterLayout | undefined;
        if (bfl && Array.isArray(bfl) && bfl.length > 0) {
          this.buildFilterLayout = bfl;
        }
      }

      // Parse user filters (shown in UI)
      const userFilters = (config['userFilters'] || config['user_filters']) as
        Record<string, unknown> | undefined;
      if (userFilters) {
        const uf = (userFilters['selectedFilters'] || userFilters['selected_filters']) as
          string[] | undefined;
        if (uf?.length) this.userFilterKeys = uf;

        // Restore saved user filter values
        const ufv = (userFilters['filterValues'] || userFilters['filter_values']) as
          Record<string, unknown> | undefined;
        if (ufv && Object.keys(ufv).length > 0) {
          this.userFilterValues = ufv;
        }

        // Restore saved user filter operators
        const ufo = (userFilters['filterOperators'] || userFilters['filter_operators']) as
          Record<string, FilterOperator> | undefined;
        if (ufo && Object.keys(ufo).length > 0) {
          this.userFilterOperators = ufo;
        }
      }

      // Parse group-by config
      const groupBy = (config['groupBy'] || config['group_by']) as
        Record<string, unknown> | undefined;
      if (groupBy) {
        const gbFields = (groupBy['selectedFields'] || groupBy['selected_fields']) as
          string[] | undefined;
        if (gbFields?.length) this.groupByFieldKeys = gbFields;

        const colAggs = (groupBy['columnAggregations'] || groupBy['column_aggregations']) as
          ColumnAggregationMap | undefined;
        if (colAggs && Object.keys(colAggs).length > 0) this.columnAggregationMap = colAggs;

        const dateIntervals = (groupBy['dateGroupingIntervals'] ||
          groupBy['date_grouping_intervals']) as DateGroupingMap | undefined;
        if (dateIntervals && Object.keys(dateIntervals).length > 0)
          this.dateGroupingIntervals = dateIntervals;
      }

      // Snapshot the saved-report filter baseline (config defaults) before
      // overlaying any locally-saved overrides, so we can reset back to it.
      this.savedUserFilterValues = { ...this.userFilterValues };
      this.savedUserFilterOperators = { ...this.userFilterOperators };

      // Overlay with user's locally-saved view state (filters + sort)
      this._restoreViewState();

      this.isLoading = false;
      await this.loadFields();

      if (this.isDestroying || this.isDestroyed) return;

      this.endReportTrackingSession();
      this.reportTrackingSession = this.reportTracking.startSession(
        reportId,
        this.args.viewedFrom ?? 'admin'
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      this.loadError = `Failed to retrieve report: ${message}`;
    } finally {
      this.isLoading = false;
    }
  };

  private endReportTrackingSession = () => {
    this.reportTrackingSession?.end();
    this.reportTrackingSession = undefined;
  };

  loadFields = async () => {
    this.isLoadingFields = true;
    this.loadError = '';

    try {
      const companyId = this.companyId || undefined;

      // WarpDrive wave 1.5: fetch the enhanced field catalog via the shared
      // builder (replaces `store.adapterFor('search').fields`); unwrap `.content`.
      const { content: response } = await this.store.request<EnhancedFieldsResponse>(
        fieldsRequestFor(this.searchRequestModelType, companyId)
      );
      this.datasetConfig = response.datasetConfig;

      this.allFields = resolveFieldsFromEnhancedEndpoint(response);

      // Resolve distinct values for all text/keyword user filters at runtime
      for (const key of this.userFilterKeys) {
        const field = this.allFields.find((f) => f.key === key);
        if (field) this.resolveDistinctValues(field);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      this.allFields = [];
      this.loadError = `Failed to load report fields: ${message}`;
    } finally {
      this.isLoadingFields = false;
    }
  };

  /**
   * The report's current query scope for the facet (distinct-values) queries:
   * build filters are hard constraints (applied even to a filter's own
   * dropdown), user filter values cascade across the other facets, and the
   * company scope + group-by mode mirror the result query.
   */
  get reportQueryContext() {
    return {
      buildFilterValues: this.buildFilterValues,
      buildFilterLayout: this.buildFilterLayout,
      userFilterValues: this.userFilterValues,
      fields: this.allFields,
      companyIds: this.activeCompanyIds,
      companyFilterField: this.companyFilterField,
      companyFilterOperator: this.companyFilterOperator,
      isAggregated: this.isAggregated,
      groupByFieldKeys: this.groupByFieldKeys,
      columns: this.selectedColumns,
    };
  }

  /** Monotonic token: responses from a superseded query context are discarded. */
  #distinctFetchEpoch = 0;

  resolveDistinctValues = (field: FieldOption) => {
    if (!shouldFetchDistinctValues(field)) return;

    const epoch = this.#distinctFetchEpoch;

    // WarpDrive wave 1.5: the resolver now drives the shared search builder via
    // the store directly (replaces the injected `searchAdapter`).
    fetchDistinctValues(
      this.store,
      field.key,
      this.companyId,
      this.reportQueryContext,
      this.searchRequestModelType,
      field.sortPath
    )
      .then((values) => {
        if (this.isDestroying || this.isDestroyed) return;
        // A newer refresh superseded this fetch — a slow response from the old
        // filter/company context must not overwrite the fresh options.
        if (epoch !== this.#distinctFetchEpoch) return;
        if (values) {
          this.distinctValuesMap = { ...this.distinctValuesMap, [field.key]: values };
        } else {
          const next = { ...this.distinctValuesMap };
          delete next[field.key];
          this.distinctValuesMap = next;
        }
      })
      .catch(() => {
        // Distinct value fetch failed — field will fall back to text input
      });
  };

  /** Re-fetch every facet's distinct values against the current query context. */
  refreshDistinctValues = () => {
    this.#distinctFetchEpoch++;
    for (const filterKey of this.userFilterKeys) {
      const field = this.allFields.find((f) => f.key === filterKey);
      if (field) this.resolveDistinctValues(field);
    }
  };

  updateFilterValue = (key: string, value: unknown) => {
    const vals = { ...this.userFilterValues, [key]: value };
    for (const [k, v] of Object.entries(vals)) {
      if (v === '' || v === undefined || v === null) {
        delete vals[k];
      }
    }
    this.userFilterValues = vals;
    this.page = 1;
    this._saveViewState();

    // Track filter interaction
    const baseKey = key.replace(
      /__(gte|lte|gt|lt|ne|in|not_in|is_empty|is_not_empty|contains|starts_with|relative_date)$/,
      ''
    );
    const fieldLabel =
      this.columnLabelMap[baseKey] ||
      this.allFields.find((f) => f.key === baseKey)?.label ||
      baseKey;
    this.reportTracking.trackFilter(key, value, fieldLabel);

    // Refresh cascading distinct values
    this.refreshDistinctValues();
  };

  removeFilter = (_field: FieldOption) => {
    // no-op in viewer — filters are fixed; values are cleared via dismissChip
  };

  updateSort = (sortField: string) => {
    const field = sortField.replace(/:(?:asc|desc)$/, '');
    const direction: 'asc' | 'desc' = sortField.endsWith(':desc') ? 'desc' : 'asc';
    this.sorts = [{ key: field, direction }];
    this._saveViewState();

    const sortLabel = this.allFields.find((f) => f.key === field)?.label || field;
    this.reportTracking.trackSort(field, direction, sortLabel);
  };

  handleAddSort = (key: string, direction: 'asc' | 'desc') => {
    this.sorts = addSort(this.sorts, key, direction);
    this._saveViewState();
  };

  handleRemoveSort = (key: string) => {
    this.sorts = removeSort(this.sorts, key);
    this._saveViewState();
  };

  handleMoveSort = (fromIndex: number, toIndex: number) => {
    this.sorts = moveSort(this.sorts, fromIndex, toIndex);
    this._saveViewState();
  };

  handleToggleSortDirection = (key: string) => {
    this.sorts = toggleSortDirection(this.sorts, key);
    this._saveViewState();
  };

  handleSetSortDirection = (key: string, direction: 'asc' | 'desc') => {
    this.sorts = setSortDirection(this.sorts, key, direction);
    this._saveViewState();
  };

  handleResetSorts = () => {
    this.sorts = [...this.savedSorts];
    this._saveViewState();
  };

  /** Restore all filter values/operators to the saved report config. */
  handleResetFilters = () => {
    this.userFilterValues = { ...this.savedUserFilterValues };
    this.userFilterOperators = { ...this.savedUserFilterOperators };
    this.page = 1;
    this._saveViewState();
    this.refreshDistinctValues();
  };

  updatePage = (page: number) => {
    this.page = page;
  };

  updateLimit = (limit: number) => {
    this.limit = limit;
    this.page = 1;
  };

  handleBack = () => {
    if (this.args.onBack) {
      this.args.onBack();
    }
  };

  /**
   * Exports run server-side but carry the viewer's CURRENT query body
   * (search_body + company_id), so the CSV matches what's on screen —
   * view-time user filters and relative-date ranges resolved at click
   * time. Structure (columns, labels, formats, filename) still derives
   * from the saved config server-side, and the body passes the same
   * SearchPolicy + company-scoping stack as preview searches — the #8841
   * data-leak fix lives in that server-side validation, not in what the
   * FE sends. Scheduled exports keep running from the saved config with
   * no FE involvement.
   *
   * Disabled while the admin multi-company selector has selections — a
   * multi-company scope has no single company_id to export under. Admins
   * export a global report from a company page instead (single scope
   * applied automatically); with no selection at all, admins export
   * all companies (SearchPolicy allows only TIO-level roles through
   * unscoped).
   */
  get canExport() {
    // No export while the report failed/was blocked from loading (e.g. the
    // partner product-scope guard) — the server has no per-product gate, so
    // the disabled button is part of the access boundary.
    if (this.loadError) return false;
    // The export body is assembled from loaded state — before the report
    // config and field metadata resolve (or when no saved column resolves
    // against the metadata), the body would not represent anything on screen.
    // canShowData is the same gate the preview table uses.
    if (this.isLoading || !this.canShowData) return false;
    // SQL datasets export via keyset batches (WS-9): same single-company
    // posture as OS — and param-kind needs its company (sqlCompanyRequired).
    if (isSqlDataset(this.modelType)) {
      return !this.sqlCompanyRequired && this.activeCompanyIds.length === 0;
    }
    return this.activeCompanyIds.length === 0;
  }

  /**
   * A param-kind SQL dataset can't be queried without a company (the
   * backend 400s and the table would just render empty). Mirrors the
   * builder's sqlCompanyRequired gate — reachable when a saved SQL report
   * is opened from the global list with no company scope.
   */
  get sqlCompanyRequired() {
    return (
      isSqlDataset(this.modelType) && !this.companyId && !isCrossCompanyCapable(this.modelType)
    );
  }

  get exportDisabled() {
    return !this.canExport || this.exportReport.isRunning;
  }

  get exportDisabledHint() {
    if (this.canExport || this.loadError) return undefined;
    if (this.isLoading || !this.canShowData) return undefined;
    if (this.sqlCompanyRequired)
      return 'Open this report from a company page — this SQL dataset is scoped to one company per report';
    return 'Open this report from a company page to export it with a company scope';
  }

  /**
   * The exact query the viewer is looking at — the same assembly used for the
   * persisted searchBody (buildReportSearchBody), fed with this session's live
   * user-filter state. Sorts ride along but the standard export scrolls in
   * _doc order server-side; only aggregated exports honor bucket ordering.
   */
  get #searchBodyOptions() {
    return {
      modelType: this.searchRequestModelType,
      fields: this.allFields,
      buildFilterValues: this.buildFilterValues,
      buildFilterLayout: this.buildFilterLayout,
      userFilterValues: this.userFilterValues,
      companyId: this.companyId,
      companyIds: this.activeCompanyIds,
      companyFilterOperator: this.companyFilterOperator,
      companyFilterField: this.companyFilterField,
      isAggregated: this.isAggregated,
      groupByFieldKeys: this.groupByFieldKeys,
      columns: this.selectedColumns,
      sorts: this.sorts,
      columnAggregationMap: this.columnAggregationMap,
      dateGroupingIntervals: this.dateGroupingIntervals,
    };
  }

  get currentSearchBody() {
    return buildReportSearchBody(this.#searchBodyOptions);
  }

  // The sql-export endpoint scopes the single company via the request param,
  // never a DSL term (param-kind catalogs may not project the tenant column, so
  // a body term is rejected). A multi-company column-kind set stays a body
  // filter — those datasets always declare the column. OS exports still use
  // currentSearchBody, which keeps the embedded term.
  get sqlExportSearchBody() {
    return buildReportSearchBody({ ...this.#searchBodyOptions, forceSqlCompanyTerm: false });
  }

  @tracked showExportFilterConfirm = false;
  @tracked exportError = '';

  /**
   * Export button entry point. Filters are the #1 source of data-report
   * confusion, so when the view-time filter values diverge from the saved
   * report the export requires an explicit confirmation spelling out what
   * will be different.
   */
  requestExport = () => {
    if (this.exportFilterValuesDiverged) {
      this.showExportFilterConfirm = true;
    } else {
      void this.exportReport.perform();
    }
  };

  confirmFilteredExport = () => {
    this.showExportFilterConfirm = false;
    void this.exportReport.perform();
  };

  cancelFilteredExport = () => {
    this.showExportFilterConfirm = false;
  };

  exportReport = dropTask(async () => {
    if (!this.reportId || !this.canExport) return;

    // Pin the report this export belongs to — if the user navigates to a
    // different report while the request is in flight, its failure must not
    // surface on the new report's view.
    const exportingReportId = this.reportId;
    this.exportError = '';
    this.reportTracking.trackExport('csv');

    // WarpDrive wave 1.5: export via the shared raw-fetch helper (tio-data is
    // owner-free, so the per-app api key is passed from the features service).
    const apiKey = this.features.ENV?.apiKey ?? '';

    // Send the on-screen query body so the CSV matches the preview (see the
    // canExport docblock for the scoping/security posture). The OS path sends
    // no column params — structure comes from the saved config; the SQL path
    // has no server-side reconstruction, so it sends columns/labels/formats
    // explicitly (below).
    try {
      if (isSqlDataset(this.modelType)) {
        // SQL datasets export on the sql-search vertical: passthrough body
        // (company scoping rides the request param, never a body term) plus the
        // explicit columns/labels/formats the on-screen table uses — the server
        // has no saved-config reconstruction here, and its ColumnFormatter has
        // no fallback, so raw cents/enum codes/ISO dates would ship without them.
        const result = await sqlExportReport(this.reportId, {
          apiKey,
          dataset: this.modelType,
          companyId: this.companyId,
          searchBody: this.sqlExportSearchBody,
          columnKeys: this.selectedColumns.map((column) => column.key),
          columnLabels: this.columnLabelMap,
          columnFormats: this.columnFormatMap,
        });
        if (result.truncated && this.reportId === exportingReportId) {
          this.exportError =
            'Export truncated at the dataset\u2019s export row cap — narrow the filters for a complete file.';
        }
      } else {
        await exportReportRequest(this.reportId, {
          apiKey,
          companyId: this.companyId,
          searchBody: this.currentSearchBody,
        });
      }
    } catch (error) {
      console.error('Export failed:', error);
      if (this.reportId === exportingReportId) {
        this.exportError = 'Export failed — please try again.';
      }
    }
  });

  handleCompanyIdsChange = (companyIds: string[]) => {
    this.companyIds = companyIds;
    this.page = 1;
    this.distinctValuesMap = {};
    this.refreshDistinctValues();
  };

  handleCompanyFilterOperatorChange = (operator: string) => {
    this.companyFilterOperator = operator === 'not_in' ? 'not_in' : 'in';
    this.page = 1;
    // With no companies selected the operator has no effect on the query —
    // skip N no-op facet re-fetches.
    if (this.activeCompanyIds.length > 0) {
      this.refreshDistinctValues();
    }
  };

  <template>
    {{! Consume watcher to keep it reactive }}
    {{this._reportIdWatcher.value}}
    <div class="tio-col-flush h-full min-h-screen tio-bg-sunken">
      {{! Header }}
      <header class="tio-bg-surface border-b tio-border-default px-6 py-4">
        <div class="tio-row-between">
          <div class="tio-row">
            {{#if @onBack}}
              <button
                type="button"
                class="p-1 rounded hover:tio-bg-sunken tio-fg-muted"
                title="Back to reports"
                {{on "click" this.handleBack}}
              >
                <PhArrowLeft @size={{20}} />
              </button>
            {{/if}}
            <PhFileText @size={{24}} class="tio-fg-secondary" />
            <h1 class="tio-text-lg-semibold tio-fg-primary">
              {{this.reportName}}
            </h1>
            {{#if this.formattedDate}}
              <span class="text-sm tio-fg-muted">
                Updated
                {{this.formattedDate}}
              </span>
            {{/if}}
          </div>
          <div class="tio-row">
            {{#if this.exportReport.isRunning}}
              <span class="tio-inline-row-tight tio-text-sm-medium tio-fg-info animate-pulse">
                <span
                  class="h-2 w-2 rounded-full tio-bg-info animate-ping"
                  aria-hidden="true"
                ></span>
                Generating export…
              </span>
            {{/if}}
            {{#if this.exportDisabledHint}}
              {{! Visible hint — title tooltips never fire on disabled buttons
                  (tio-btn[disabled] sets pointer-events: none) }}
              <span class="text-xs tio-fg-muted" data-test-export-disabled-hint>
                {{this.exportDisabledHint}}
              </span>
            {{/if}}
            {{#if this.exportError}}
              {{! role=alert — inserted asynchronously; announce the failure }}
              <span class="text-xs tio-fg-danger" role="alert" data-test-export-error>
                {{this.exportError}}
              </span>
            {{/if}}
            <Button
              @appearance="outlined"
              @intent="primary"
              @icon={{PhDownloadSimple}}
              @isRunning={{this.exportReport.isRunning}}
              @isDisabled={{this.exportDisabled}}
              data-test-export-button
              {{on "click" this.requestExport}}
            >
              {{if this.exportReport.isRunning "Exporting..." "Export"}}
            </Button>
          </div>

          <ConfirmationDialog
            @isOpen={{this.showExportFilterConfirm}}
            @title="Export with current filters?"
            @intent="warning"
            @confirmText="Export anyway"
            @onConfirm={{this.confirmFilteredExport}}
            @onCancel={{this.cancelFilteredExport}}
          >
            <div class="tio-col">
              {{#if this.hasActiveChips}}
                <p class="text-sm tio-fg-secondary">
                  Your filters differ from the saved report. The export will use these filter values
                  in place of the report's saved filters:
                </p>
                <ul class="tio-col-tight p-3 rounded-lg tio-bg-sunken">
                  {{#each this.activeFilterChips as |chip|}}
                    <li class="text-sm tio-fg-primary" data-test-export-confirm-chip>
                      <PhFunnel @size={{14}} class="inline tio-fg-muted" />
                      <span class="tio-text-sm-medium">{{chip.label}}:</span>
                      {{chip.value}}
                    </li>
                  {{/each}}
                </ul>
              {{else}}
                <p class="text-sm tio-fg-secondary">
                  Your filters differ from the saved report. No filter values are active, so the
                  export applies only the report's built-in scope.
                </p>
              {{/if}}
              {{#if this.hasClearedSavedFilterValues}}
                <p class="text-sm tio-fg-warning-body" data-test-export-confirm-cleared>
                  You've cleared saved filter values — the export will include rows the saved report
                  filters out.
                </p>
              {{/if}}
            </div>
          </ConfirmationDialog>
        </div>
      </header>

      <div class="flex-1 overflow-hidden">
        {{#if this.loadError}}
          <div class="p-6">
            <div
              class="p-4 tio-bg-danger-soft border tio-border-danger rounded-lg tio-fg-danger-h text-sm"
            >
              {{this.loadError}}
            </div>
          </div>
        {{else if this.isLoading}}
          <div class="tio-row-center h-48 tio-fg-muted text-sm">
            Loading report...
          </div>
        {{else}}
          <div class="flex h-full">
            {{! ── Filter Sidebar ── }}
            {{#if this.hasUserFilters}}
              <div
                class="shrink-0 border-r tio-border-default tio-bg-surface tio-col-flush transition-all duration-200
                  {{if this.isFilterSidebarOpen 'w-72' 'w-10'}}"
              >
                {{! Sidebar header with toggle }}
                <div class="tio-row-between px-3 py-3 border-b tio-border-subtle">
                  {{#if this.isFilterSidebarOpen}}
                    <div class="tio-row-snug tio-text-sm-semibold tio-fg-secondary">
                      <PhFunnel @size={{14}} />
                      <span>Filters</span>
                    </div>
                  {{/if}}
                  <button
                    type="button"
                    class="p-1 rounded hover:tio-bg-sunken tio-fg-muted"
                    aria-label={{if this.isFilterSidebarOpen "Collapse filters" "Expand filters"}}
                    aria-expanded={{if this.isFilterSidebarOpen "true" "false"}}
                    {{on "click" this.toggleFilterSidebar}}
                  >
                    {{#if this.isFilterSidebarOpen}}
                      <PhCaretLeft @size={{14}} />
                    {{else}}
                      <PhSlidersHorizontal @size={{16}} />
                    {{/if}}
                  </button>
                </div>

                {{! Filter controls (visible when open) }}
                {{#if this.isFilterSidebarOpen}}
                  <div class="flex-1 overflow-y-auto p-3 space-y-4">
                    {{#if this.isFilterDirty}}
                      <button
                        type="button"
                        class="tio-inline-row gap-1.5 rounded-md border tio-border-warning tio-bg-warning-soft hover:tio-bg-warning-soft-hover px-2.5 py-1 tio-text-xs-medium tio-fg-warning-h"
                        data-test-filter-reset-chip
                        {{on "click" this.handleResetFilters}}
                      >
                        <PhArrowCounterClockwise @size={{12}} />
                        Filters modified · Reset to saved
                      </button>
                    {{/if}}
                    {{#each this.userFilters as |field|}}
                      {{#let (this.detectOperator field) as |currentOp|}}
                        <div class="space-y-1.5">
                          <span class="tio-text-xs-medium tio-fg-muted">{{this.getFieldDisplayLabel
                              field
                            }}</span>
                          <Select
                            @items={{this.getOperatorItems field}}
                            @selected={{currentOp}}
                            @onChange={{fn this.handleOperatorChange field.key}}
                            @size="sm"
                            @searchable={{false}}
                            @clearable={{false}}
                          />

                          {{! Value input — varies by operator }}
                          {{#if (this.isNoValueOperator currentOp)}}
                            <p class="text-xs tio-fg-muted italic">
                              {{if
                                (eq currentOp "is_empty")
                                "Matches records with no value"
                                "Matches records with a value"
                              }}
                            </p>
                          {{else if (this.isRelativeDateOp currentOp)}}
                            <Select
                              @items={{this.relativeDateItems}}
                              @selected={{this.getValueForOperator field}}
                              @onChange={{fn this.handleRelativeDateChange field.key}}
                              @placeholder="Select a time range…"
                              @size="sm"
                              @clearable={{true}}
                              @searchable={{true}}
                            />
                          {{else if (this.isBetweenOp currentOp)}}
                            {{#if (this.isDateType field)}}
                              <div class="space-y-1">
                                <Flatpickr
                                  @date={{if
                                    (this.getFilterRangeStart field.key)
                                    (this.getFilterRangeStart field.key)
                                    ""
                                  }}
                                  @onChange={{fn this.handleDateStartChange field.key}}
                                  @dateFormat="Y-m-d"
                                  @altInput={{true}}
                                  @altFormat="M j, Y"
                                  @minDate={{this.dateWindowMin field}}
                                  @maxDate={{this.dateWindowMax field}}
                                  placeholder="Start date"
                                  class="tio-form-input text-sm w-full"
                                />
                                <Flatpickr
                                  @date={{if
                                    (this.getFilterRangeEnd field.key)
                                    (this.getFilterRangeEnd field.key)
                                    ""
                                  }}
                                  @onChange={{fn this.handleDateEndChange field.key}}
                                  @dateFormat="Y-m-d"
                                  @altInput={{true}}
                                  @altFormat="M j, Y"
                                  @minDate={{this.dateRangeEndMin field}}
                                  @maxDate={{this.dateWindowMax field}}
                                  placeholder="End date"
                                  class="tio-form-input text-sm w-full"
                                />
                              </div>
                            {{else}}
                              <div class="space-y-1">
                                <Input
                                  @type="number"
                                  @size="sm"
                                  @value={{this.getFilterRangeStart field.key}}
                                  @onChange={{fn this.handleNumberMinChange field.key}}
                                  @placeholder="Min"
                                />
                                <Input
                                  @type="number"
                                  @size="sm"
                                  @value={{this.getFilterRangeEnd field.key}}
                                  @onChange={{fn this.handleNumberMaxChange field.key}}
                                  @placeholder="Max"
                                />
                              </div>
                            {{/if}}
                          {{else if (this.isMultiSelectOp currentOp)}}
                            {{#if (this.hasDistinctValues field)}}
                              <Select
                                @items={{this.getDistinctItems field}}
                                @multiple={{true}}
                                @selectedKeys={{this.getMultiSelectValues field}}
                                @onChangeKeys={{fn
                                  this.handleMultiSelectChange
                                  field.key
                                  currentOp
                                }}
                                @placeholder="Select values…"
                                @size="sm"
                                @clearable={{true}}
                                @searchable={{true}}
                              />
                            {{else if (this.hasEnumValues field)}}
                              <Select
                                @items={{this.getEnumSelectItems field}}
                                @multiple={{true}}
                                @selectedKeys={{this.getMultiSelectValues field}}
                                @onChangeKeys={{fn
                                  this.handleMultiSelectChange
                                  field.key
                                  currentOp
                                }}
                                @placeholder="Select values…"
                                @size="sm"
                                @clearable={{true}}
                                @searchable={{true}}
                              />
                            {{else}}
                              <Input
                                @size="sm"
                                @value={{this.getValueForOperator field}}
                                @onChange={{fn this.handleValueForOperator field.key currentOp}}
                                @placeholder="Comma-separated values…"
                              />
                            {{/if}}
                          {{else if (this.isBooleanType field)}}
                            <Select
                              @items={{array
                                (hash key="true" label="Yes")
                                (hash key="false" label="No")
                              }}
                              @selected={{this.getFilterValue field.key}}
                              @onChange={{fn this.handleBooleanChange field.key}}
                              @placeholder="Any"
                              @size="sm"
                              @clearable={{true}}
                            />
                          {{else if (this.hasEnumValues field)}}
                            <Select
                              @items={{this.getEnumSelectItems field}}
                              @selected={{this.getValueForOperator field}}
                              @onChange={{fn this.handleValueForOperator field.key currentOp}}
                              @placeholder="All"
                              @size="sm"
                              @clearable={{true}}
                              @searchable={{true}}
                            />
                          {{else if (this.isDateType field)}}
                            <Flatpickr
                              @date={{if
                                (this.getValueForOperator field)
                                (this.getValueForOperator field)
                                ""
                              }}
                              @onChange={{fn this.handleDateValueForOperator field.key currentOp}}
                              @dateFormat="Y-m-d"
                              @altInput={{true}}
                              @altFormat="M j, Y"
                              @minDate={{this.dateWindowMin field}}
                              @maxDate={{this.dateWindowMax field}}
                              placeholder="Select a date"
                              class="tio-form-input text-sm w-full"
                            />
                          {{else if (this.isNumericType field)}}
                            <Input
                              @type="number"
                              @size="sm"
                              @value={{this.getValueForOperator field}}
                              @onChange={{fn this.handleNumberForOperator field.key currentOp}}
                              @placeholder="Enter a number…"
                            />
                          {{else if (this.isTextType field)}}
                            {{#if (this.hasDistinctValues field)}}
                              <Select
                                @items={{this.getDistinctItems field}}
                                @selected={{this.getValueForOperator field}}
                                @onChange={{fn this.handleValueForOperator field.key currentOp}}
                                @placeholder="All"
                                @size="sm"
                                @clearable={{true}}
                                @searchable={{true}}
                              />
                            {{else}}
                              <Input
                                @size="sm"
                                @value={{this.getValueForOperator field}}
                                @onChange={{fn this.handleValueForOperator field.key currentOp}}
                                @placeholder="Enter value…"
                              />
                            {{/if}}
                          {{else}}
                            <Input
                              @size="sm"
                              @value={{this.getValueForOperator field}}
                              @onChange={{fn this.handleValueForOperator field.key currentOp}}
                              @placeholder="Enter value…"
                            />
                          {{/if}}

                          {{#let (this.dateWindowWarning field) as |dateWarning|}}
                            {{#if dateWarning}}
                              <p
                                class="tio-text-xs-medium tio-fg-warning mt-1"
                                data-test-report-view-date-window-warning
                              >
                                {{dateWarning}}
                              </p>
                            {{/if}}
                          {{/let}}
                        </div>
                      {{/let}}
                    {{/each}}
                  </div>
                {{/if}}
              </div>
            {{/if}}

            {{! ── Main Content ── }}
            <div class="flex-1 overflow-auto p-6 space-y-4">
              {{! Company Selector for global reports }}
              {{#if this.needsCompanySelector}}
                <div class="tio-surface-panel">
                  <div class="tio-wrap items-end">
                    <div class="tio-col-extra-tight w-36">
                      <span class="tio-text-xs-medium tio-fg-muted">Company</span>
                      <Select
                        @items={{this.companyFilterOperatorOptions}}
                        @selected={{this.companyFilterOperator}}
                        @onChange={{this.handleCompanyFilterOperatorChange}}
                        @size="sm"
                        @clearable={{false}}
                      />
                    </div>
                    <div class="tio-col-extra-tight min-w-80 max-w-xl flex-1">
                      <span class="tio-text-xs-medium tio-fg-muted">Companies</span>
                      <Select
                        @items={{this.companies}}
                        @multiple={{true}}
                        @selectedKeys={{this.selectedCompanyIds}}
                        @onChangeKeys={{this.handleCompanyIdsChange}}
                        @placeholder="All companies"
                        @size="sm"
                        @searchable={{true}}
                        @clearable={{true}}
                      />
                    </div>
                  </div>
                </div>
              {{/if}}

              {{! Active filter chips }}
              {{#if this.hasActiveChips}}
                <div class="tio-wrap-tight">
                  {{#each this.activeFilterChips as |chip|}}
                    <span
                      class="tio-inline-row-extra-tight tio-bg-info-soft tio-fg-info-h rounded-full px-3 py-1 text-sm"
                    >
                      <span class="font-medium">{{chip.label}}:</span>
                      <span>{{chip.value}}</span>
                      <button
                        type="button"
                        class="ml-0.5 p-0.5 rounded-full hover:tio-bg-info-soft-hover hover:tio-fg-info-h"
                        title="Clear filter"
                        {{on "click" (fn this.dismissChip chip.key)}}
                      >
                        <PhX @size={{12}} />
                      </button>
                    </span>
                  {{/each}}
                </div>
              {{/if}}

              {{! Data Table or loading/empty states }}
              {{#if this.isLoadingFields}}
                <div class="tio-row-center h-48 tio-fg-muted text-sm">
                  Loading report data...
                </div>
              {{else if this.canShowData}}
                {{#if this.isSortDirty}}
                  <div class="tio-flex-end">
                    <button
                      type="button"
                      class="tio-inline-row gap-1.5 rounded-md border tio-border-warning tio-bg-warning-soft hover:tio-bg-warning-soft-hover px-2.5 py-1 tio-text-xs-medium tio-fg-warning-h"
                      data-test-sort-reset-chip
                      {{on "click" this.handleResetSorts}}
                    >
                      <PhArrowCounterClockwise @size={{12}} />
                      Sort modified · Reset to saved
                    </button>
                  </div>
                {{/if}}
                {{#if this.hasMultipleSorts}}
                  <SortPanel
                    @sorts={{this.sorts}}
                    @columns={{this.selectedColumns}}
                    @onAdd={{this.handleAddSort}}
                    @onRemove={{this.handleRemoveSort}}
                    @onMove={{this.handleMoveSort}}
                    @onToggleDirection={{this.handleToggleSortDirection}}
                    @onSetDirection={{this.handleSetSortDirection}}
                    @onReset={{this.handleResetSorts}}
                    @savedSorts={{this.savedSorts}}
                    @isAggregated={{this.isAggregated}}
                    @groupByFieldKeys={{this.groupByFieldKeys}}
                  />
                {{/if}}
                {{#if this.sqlCompanyRequired}}
                  <div
                    class="tio-row-center h-48 tio-fg-muted text-sm tio-bg-surface rounded-lg border tio-border-default"
                    data-test-report-view-sql-company-required
                  >
                    Open this report from a company page — this SQL dataset is scoped to one company
                    per report.
                  </div>
                {{else}}
                  <PreviewTable
                    @columns={{this.selectedColumns}}
                    @allFields={{this.allFields}}
                    @filterValues={{this.userFilterValues}}
                    @buildFilterValues={{this.buildFilterValues}}
                    @buildFilterLayout={{this.buildFilterLayout}}
                    @companyId={{this.companyId}}
                    @companyIds={{this.activeCompanyIds}}
                    @companyFilterOperator={{this.companyFilterOperator}}
                    @companyFilterField={{this.companyFilterField}}
                    @modelType={{this.searchRequestModelType}}
                    @sorts={{this.sorts}}
                    @page={{this.page}}
                    @limit={{this.limit}}
                    @onSort={{this.updateSort}}
                    @onAddSort={{this.handleAddSort}}
                    @onToggleDirection={{this.handleToggleSortDirection}}
                    @onPageChange={{this.updatePage}}
                    @onLimitChange={{this.updateLimit}}
                    @readOnly={{true}}
                    @isAggregated={{this.isAggregated}}
                    @groupByFieldKeys={{this.groupByFieldKeys}}
                    @columnAggregationMap={{this.columnAggregationMap}}
                    @dateGroupingIntervals={{this.dateGroupingIntervals}}
                    @columnFormatMap={{this.columnFormatMap}}
                    @columnLabelMap={{this.columnLabelMap}}
                    @columnAltTextMap={{this.columnAltTextMap}}
                    @appContext={{this.appContext}}
                  />
                {{/if}}
              {{else}}
                <div
                  class="tio-row-center h-48 tio-fg-muted text-sm tio-bg-surface rounded-lg border tio-border-default"
                >
                  Unable to load report data.
                </div>
              {{/if}}
            </div>
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}

export default ReportViewPage;
